// react
import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';

// components
import Button from '@/components/atoms/Button/Button';
import { CardContainer } from '@/components/templates';
import BaseField, { FieldProps } from '../BaseField/BaseField';
import { Option } from '../Select/Select';

// types
interface TimePickerProps extends FieldProps<string> {
  value?: string;
  options: Option[];
  className?: string;
  onChange?: (value: string) => void;
}

export const TimePicker = ({
  value,
  options,
  className,
  onChange,
  id,
  label,
  description,
  tooltip,
  isRequired = false,
  isHideTitle = false,
}: TimePickerProps) => {
  // states
  const [selectedTime, setSelectedTime] = useState<string | undefined>(value);

  // effects
  useEffect(() => {
    setSelectedTime(value);
  }, [value]);

  // methods
  const handleTimeChange = useCallback(
    (option: string) => {
      if (onChange) {
        onChange(option);
      }
      setSelectedTime(option);
    },
    [onChange],
  );

  return (
    <BaseField
      id={id}
      className={className}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Wrapper>
        <CardContainer maxColumns={1} size='sm'>
          {options.map((option, index) => (
            <StyledButton
              key={index}
              ariaLabel={option.label}
              variant={selectedTime === option.value ? 'primary' : 'secondary'}
              isRectangle
              isSelected={selectedTime === option.value}
              onPress={() => handleTimeChange(option.value)}
            >
              {option.label}
            </StyledButton>
          ))}
        </CardContainer>
      </Wrapper>
    </BaseField>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[8]};
  margin-bottom: ${(props) => props.theme.spacing[8]};
  max-height: 45%;
  overflow: auto;
`;

const StyledButton = styled(Button)<{ isSelected: boolean }>`
  margin-bottom: ${(props) => props.theme.spacing[16]};
  color: ${(props) =>
    props.isSelected
      ? props.theme.color.neutral[10]
      : props.theme.color.primary[90]};
`;

TimePicker.displayName = 'TimePicker';

export default TimePicker;
